import { FloatingPanel } from '@hue/design-system';
import { Link, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { CircleHelp, Workflow, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { pageDocumentation } from './page-help-content.js';
import { companySections } from './company-sections.js';
import { PageHelpContext } from './page-help-context.js';
import { pageKeyOf } from './page-help-key.js';
import { SystemDocumentation } from './system-documentation.js';
import { documentationPageHelp, type DocumentationPageKey } from './system-documentation-flows.js';

export function PageHelpProvider({
  pathname,
  companyKey,
  children,
}: {
  pathname: string;
  companyKey: string;
  children: ReactNode;
}) {
  const page = pageKeyOf(pathname, companyKey);
  return (
    <PageHelpContext.Provider
      value={page ? <PageHelpPopover key={page} page={page} companyKey={companyKey} /> : null}
    >
      {children}
    </PageHelpContext.Provider>
  );
}

function PageHelpPopover({ page, companyKey }: { page: DocumentationPageKey; companyKey: string }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const id = useId();
  const target = documentationPageHelp[page];
  const content = pageDocumentation(page);
  const dismiss = () => setOpen(false);
  useEffect(() => {
    if (!open) return;
    close.current?.focus();
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        anchor.current?.focus();
      }
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [open]);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-label={`Como funciona: ${target.name}`}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-haspopup="dialog"
        onClick={() => setOpen(!open)}
        className="grid size-10 shrink-0 place-items-center rounded-xl border border-surface-border bg-surface-card text-content-secondary transition hover:border-brand-500/60 hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      >
        <CircleHelp className="size-[18px]" aria-hidden="true" />
      </button>
      {open ? (
        <FloatingPanel
          anchorRef={anchor}
          onDismiss={dismiss}
          align="end"
          id={id}
          role="dialog"
          aria-label={`Ajuda: ${target.name}`}
          className="w-[28rem] max-w-[calc(100vw-1rem)] max-h-[70vh] rounded-xl border border-surface-border bg-surface-card p-4 text-content shadow-card"
        >
          <div className="flex items-center justify-between gap-3 border-b border-surface-border pb-3">
            <h2 className="font-semibold">{target.name}</h2>
            <button
              ref={close}
              type="button"
              aria-label="Fechar ajuda"
              onClick={() => {
                dismiss();
                anchor.current?.focus();
              }}
              className="grid size-10 place-items-center rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>
          <ul className="my-4 list-disc space-y-3 pl-5 text-sm leading-6">
            {content?.usage.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
          <Link
            to="/companies/$companyKey/documentation"
            params={{ companyKey }}
            search={{ from: page }}
            onClick={dismiss}
            className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-brand-500 px-4 text-sm font-semibold text-on-brand focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <Workflow className="size-4" aria-hidden="true" />
            {target.flow ? 'Ver fluxo na documentação' : 'Ver documentação da área'}
          </Link>
        </FloatingPanel>
      ) : null}
    </>
  );
}

/// A Ajuda da empresa: chega com a página de origem (`?from=`), volta para ela e abre as telas
/// citadas nos fluxos.
export function CompanyDocumentation() {
  const router = useRouter();
  const { companyKey = '' } = useParams({ strict: false });
  const { from } = useSearch({ strict: false });
  const origin = from && from in documentationPageHelp ? (from as DocumentationPageKey) : undefined;
  return (
    <SystemDocumentation
      from={origin}
      onBack={() => router.history.back()}
      renderOpen={(page, label) => {
        const to =
          page === 'new-sale'
            ? '/companies/$companyKey/sales/new'
            : companySections.find((section) => section.key === page)!.to;
        return (
          <Link
            to={to}
            params={{ companyKey }}
            className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-brand-500 px-4 text-sm font-semibold text-on-brand transition hover:bg-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            {label}
          </Link>
        );
      }}
    />
  );
}
